import * as rxjs from 'rxjs';

import * as Elevated from '@pitaman71/omniglot-introspect';

import * as Objects from './Objects';
import * as Stores from './Stores';
import * as Properties from './Properties';
import * as Relations from './Relations';
import * as Streams from './Streams';
import Dependencies from './Dependencies';
import { BaseMutation, OfScalar, OfSet, OfSequence, OfMap, OfRelation } from './Mutations';

export let debugMode = false;

export abstract class BaseMutator<MutationType extends BaseMutation> {
    fromParent: rxjs.Subject<MutationType>;
    lastFromParent: Elevated.Reference<MutationType>;
    fromPeer: rxjs.Subject<{ peerId: string, mutation: MutationType }>;
    persistKey: Stores.Key<any>;
    peerId: string = Elevated.factories.makeObjectId();
    persist: Stores.Persist;

    constructor(
        persistKey: Stores.Key<any>,
        persist: Stores.Persist
    ) {
        this.fromParent = Dependencies.makeSubject();
        this.lastFromParent = Elevated.Reference.fromNull();
        this.fromPeer = Dependencies.makeSubject();
        this.persistKey = persistKey;
        this.persist = persist;
    }

    pull() {
        return this.persist.pull(
            this.persistKey,
            this.lastFromParent,
            this.fromParent
        ).then(lastFromParent_ => {
            if(!lastFromParent_._def?.isPrime)
                this.lastFromParent = lastFromParent_;
        });
    }

    watch(peerId: string) { 
        const mutator = this;
        return new class {
            mutations(observer: rxjs.Observer<MutationType>) { 
                const subscriptions = new Streams.Subscriptions();
                if(debugMode) console.log(`${mutator.peerId} : (PersistZone.BaseMutator) now watched by ${peerId}`);
                subscriptions.add(mutator.fromParent.subscribe({
                    next: (item) => {
                        if(debugMode) {
                            console.log(`PollStoreMutators(${mutator.peerId}) receives ${item} from parent`)
                        }
                        observer.next(item)
                    }, error: (err: any) => {
                        observer.error(err);
                    }, complete: () => {
                        observer.complete();
                    }
                }));
                mutator.pull();
                subscriptions.add(mutator.persist.onPullRequest.subscribe(() => {
                    mutator.pull();
                }));
                subscriptions.add(mutator.fromPeer.subscribe({
                    next: (update: { peerId: string, mutation: MutationType }) => update.peerId == mutator.peerId || observer.next(update.mutation)
                }))
                return subscriptions;
            }
        }
    }

    stateful() { 
        return undefined;
    }

    persistent() { 
        const mutator = this;
        return new class implements Streams.Persistent<MutationType> {
            save(peerId: string, ... suffix: MutationType[]) {
                suffix.forEach(mutation => mutator.fromPeer.next({ peerId, mutation }));
                return mutator._commit(peerId, ... suffix);
            }        
        };
    }

    abstract _reset(): MutationType;
    abstract _commit(peerId: string, ...suffix: MutationType[]): Promise<MutationType>;
}

export abstract class PropertyMutator<PropertyTypeParams extends Properties.TypeParams, MutationType extends BaseMutation>  extends BaseMutator<MutationType> {
    property: Properties.Instance<PropertyTypeParams>;
    domain: PropertyTypeParams["Domain"];
    abstract factory: Elevated.Factory<MutationType>;

    constructor(
        property: Properties.Instance<PropertyTypeParams>,
        domain: PropertyTypeParams["Domain"],
        persist: Stores.Persist
    ) {
        super(Stores.Key.fromProperty(property.descriptor, property.binding), persist);
        this.property = property;
        this.domain = domain;
    }

    _commit(peerId: string, ...suffix: MutationType[]): Promise<MutationType> {
        suffix.forEach(mutation => this.fromPeer.next({ peerId, mutation }));
        return this.persist.push(this.persistKey, ...suffix);
    }
}

export class ScalarMutator<PropertyTypeParams extends Properties.TypeParams> extends PropertyMutator<PropertyTypeParams, OfScalar.Mutation<PropertyTypeParams["Value"]>> implements Streams.ScalarStream<PropertyTypeParams["Value"], PropertyTypeParams["Domain"]> {
    factory = OfScalar.Mutation.Factory;

    _reset(): OfScalar.Mutation<PropertyTypeParams["Value"]> {
        return OfScalar.Mutation.fromClear(null);
    }
}

export class SetMutator<PropertyTypeParams extends Properties.TypeParams> extends PropertyMutator<PropertyTypeParams, OfSet.Mutation<PropertyTypeParams["Value"]>> implements Streams.SetStream<PropertyTypeParams["Value"], PropertyTypeParams["Domain"]> {
    factory = OfSet.Mutation.Factory;

    _reset(): OfSet.Mutation<PropertyTypeParams["Value"]> {
        return OfSet.Mutation.fromClear(null);
    }
}

export class SequenceMutator<PropertyTypeParams extends Properties.TypeParams> extends PropertyMutator<PropertyTypeParams, OfSequence.Mutation<PropertyTypeParams["Value"]>> implements Streams.SequenceStream<PropertyTypeParams["Value"], PropertyTypeParams["Domain"]> {
    factory = OfSequence.Mutation.Factory;

    _reset(): OfSequence.Mutation<PropertyTypeParams["Value"]> {
        return OfSequence.Mutation.fromClear(null);
    }
}

export class MapMutator<KeyType, PropertyTypeParams extends Properties.TypeParams> extends PropertyMutator<PropertyTypeParams, OfMap.Mutation<KeyType, PropertyTypeParams["Value"]>> implements Streams.MapStream<KeyType, PropertyTypeParams["Value"], PropertyTypeParams["Domain"]> {
    factory = OfMap.Mutation.Factory;
    makeKey: () => KeyType;

    constructor(
        property: Properties.Instance<PropertyTypeParams>,
        makeKey: () => KeyType,
        domain: PropertyTypeParams["Domain"],
        persist: Stores.Persist
    ) {
        super(property, domain, persist);
        this.makeKey = makeKey;
    }

    _reset(): OfMap.Mutation<KeyType, PropertyTypeParams["Value"]> {
        return OfMap.Mutation.fromClear(null);
    }
}

export class RelationMutator<RelationBindingType extends Objects.BindingType> extends BaseMutator<OfRelation.Mutation<RelationBindingType>> implements Streams.RelationStream<RelationBindingType> {
    anchor: Relations.Anchor<RelationBindingType>;
    factory = OfRelation.Mutation.Factory;
    updates: rxjs.Subject<OfRelation.Mutation<RelationBindingType>>;

    constructor(
        anchor: Relations.Anchor<RelationBindingType>,
        persist: Stores.Persist
    ) {
        super(Stores.Key.fromRelation(anchor.descriptor, anchor.binding), persist);
        this.anchor = anchor;
        this.updates = Dependencies.makeSubject();
    }

    _reset(): OfRelation.Mutation<RelationBindingType> {
        return OfRelation.Mutation.fromClear(null);
    }

    _commit(peerId: string, ...suffix: OfRelation.Mutation<RelationBindingType>[]) : Promise<OfRelation.Mutation<RelationBindingType>> {
        suffix.forEach(mutation => this.fromPeer.next({ peerId, mutation }));
        return this.persist.push(this.persistKey, ...suffix);
    }
    on(actions: {
        clear: () => void,
        insert: (entry_: RelationBindingType) => void,
        remove: (entry_: RelationBindingType) => void,
    }): void {
        this.watch(this.peerId).mutations({ 
            next: (mutation_) => {
                if(mutation_.clear) actions.clear();
                else if(mutation_.assign) {
                    actions.clear();
                    mutation_.assign.entries.forEach(entry_ => actions.insert(entry_));
                } else if(mutation_.insert) {
                    mutation_.insert.entries.forEach(entry_ => actions.insert(entry_));
                } else if(mutation_.remove) {
                    mutation_.remove.entries.forEach(entry_ => actions.remove(entry_));
                }
            },
            error: () => {},
            complete: () => {}
        })
    }
}

export class PersistZone extends Stores.Zone {
    _persist: Stores.Persist;

    constructor(persist: Stores.Persist) {
        super();
        this._persist = persist;
    }

    streams() {
        const zone = this;
        return new class extends Streams.Factory {
            property<PropertyTypeParams extends Properties.TypeParams>(
                instance: Properties.Instance<PropertyTypeParams>
            ): Streams.PropertyStream<PropertyTypeParams> {
                const persistKey = Stores.Key.fromProperty(instance.descriptor, instance.binding)
                let result: Streams.PropertyStream<PropertyTypeParams>|undefined = zone._properties.get(persistKey.toString());
                if(result === undefined) {
                    instance.descriptor.build(new class implements Properties.Builder<PropertyTypeParams> {
                        object(objectSymbol: keyof PropertyTypeParams): void {}
                        measure(domain: PropertyTypeParams["Domain"]): void {}
                        example(maker: () => PropertyTypeParams["Value"]): void {}

                        scalar(): void {
                            result = { descriptor: instance.descriptor, instance, scalar: new ScalarMutator(instance, instance.descriptor.getDomain(), zone._persist) };
                        }
                        set(): void {
                            result = { descriptor: instance.descriptor, instance, set: new SetMutator(instance, instance.descriptor.getDomain(), zone._persist) };
                        }
                        sequence(): void {
                            result = { descriptor: instance.descriptor, instance, sequence: new SequenceMutator(instance, instance.descriptor.getDomain(), zone._persist) };
                        }
                        map<KeyType>(makeKey: () => KeyType): void {
                            result = { descriptor: instance.descriptor, instance, map: new MapMutator(instance, makeKey, instance.descriptor.getDomain(), zone._persist) };
                        }
                    } )
                    if(result === undefined) {
                        throw new Error('Malformed Descriptor')
                    }        
                    zone._properties.set(persistKey.toString(), result);
                }
                return result;
            }
                    
            relation<BindingType_ extends Objects.BindingType>(
                anchor: Relations.Anchor<BindingType_>
            ): Streams.RelationStream<BindingType_> {
                const persistKey = Stores.Key.fromRelation(anchor.descriptor, anchor.binding)
                let result: Streams.RelationStream<BindingType_>|undefined = zone._relations.get(persistKey.toString());
                if(result === undefined) {
                    result = new RelationMutator(anchor, zone._persist);
                    if(result === undefined) {
                        throw new Error('Malformed Descriptor')
                    }        
                    zone._relations.set(persistKey.toString(), result);
                }
                return result;
            }
        }
    }
}
